import { useTeamNumbers } from "@/lib/team-numbers";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  Activity, Brain, Trophy, HeartPulse, Bone, Dumbbell,
  Library, Rocket, Bot, Award, Gift, MessageCircle, Lock,
  Star, ArrowRight, ShieldCheck, Clock, EyeOff,
  Users, CheckCircle2, AlertTriangle, Users2,
} from "lucide-react";
import logoImg from "@/assets/logo-projeto-fisio-completo.png";
import posOrtopedia from "@/assets/pos/thumb-ortopedia.webp";
import posDor from "@/assets/pos/thumb-dor.webp";
import posEsporte from "@/assets/pos/thumb-esporte.webp";
import posGeronto from "@/assets/pos/thumb-geronto.webp";
import posTraumato from "@/assets/pos/thumb-traumato.webp";
import posPrescricao from "@/assets/pos/thumb-prescricao.webp";
import bonusStarCursos from "@/assets/bonus-star-cursos.png";
import bonusFisioEmpreende from "@/assets/bonus-fisio-empreende.png";
import bonusFeoIa from "@/assets/bonus-feo-ia.png";
import bonusComunidade from "@/assets/bonus-comunidade.png";
import logoFeo from "@/assets/logo-feo.png";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { useSiteLink } from "@/lib/managed-links/use-site-link";
import { FisioCompletoQualifier } from "@/components/fisiocompleto/FisioCompletoQualifier";


export const FAQ_ITEMS = [
  { q: "Quanto custa falar com o nosso time?", a: "Falar com nosso time é 100% gratuito e sem compromisso. Você só paga se decidir aceitar a oferta apresentada no atendimento." },
  { q: "Por que o valor não está na página?", a: "Porque a condição é exclusiva e apresentada apenas em atendimento direto com nosso time." },
  { q: "O que está incluso no Projeto Fisio Completo?", a: "Pós-graduação MEC à sua escolha + Star Cursos (assinatura anual com +120 cursos) + Fisio Empreende + Agente de IA Fukusawa + acesso à Comunidade FEO." },
  { q: "Como faço para me inscrever?", a: "Clique em qualquer botão da página para falar com nosso time. Eles te apresentam a oferta completa e liberam o link de inscrição." },
  { q: "Poderei tirar dúvidas?", a: "Sim! O atendimento é direto com nosso time, que esclarece todas as suas dúvidas antes de qualquer decisão." },
  { q: "Quem pode realizar a matrícula?", a: "A oferta é exclusiva para fisioterapeutas formados. Lembrando que são apenas 10 vagas, então é importante falar com o time o quanto antes." },
];

type FisioCompletoMode = "captura" | "wpp";

type CtaCtx = { mode: FisioCompletoMode; whatsappUrl: string; open: () => void };
const CtaContext = createContext<CtaCtx | null>(null);

function useCta() {
  const ctx = useContext(CtaContext);
  if (!ctx) throw new Error("CtaContext missing");
  return ctx;
}

function CtaAction({
  className,
  children,
  "aria-label": ariaLabel,
}: {
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
}) {
  const { mode, whatsappUrl, open } = useCta();
  if (mode === "wpp") {
    return (
      <a href={whatsappUrl} target="_blank" rel="noreferrer" className={className} aria-label={ariaLabel}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={open} className={className} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

export function FisioCompletoPage({ mode = "captura" }: { mode?: FisioCompletoMode }) {
  const whatsappUrl = useSiteLink("link.cta.wa-fisiocompleto", WHATSAPP_URL_DEFAULT);
  const [formOpen, setFormOpen] = useState(false);

  function open() {
    if (typeof window !== "undefined") {
      const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: "ViewContent",
        content_name: "Projeto Fisio Completo",
        content_category: "fisiocompleto",
        source_page: "/fisiocompleto",
      });
    }
    setFormOpen(true);
  }

  return (
    <CtaContext.Provider value={{ mode, whatsappUrl, open }}>
      <PageBody />
      {mode === "captura" && (
        <FisioCompletoQualifier
          open={formOpen}
          onOpenChange={setFormOpen}
          whatsappUrl={whatsappUrl}
        />
      )}
    </CtaContext.Provider>
  );
}

const WHATSAPP_URL_DEFAULT = "https://nimble.li/l9en6zo9";

const POS = [
  { name: "Fisioterapia em Ortopedia e Trauma", image: posOrtopedia },
  { name: "Especialização em Dor", image: posDor },
  { name: "Fisioterapia em Esporte", image: posEsporte },
  { name: "Fisioterapia em Gerontologia", image: posGeronto },
  { name: "Fisioterapia em Traumatologia", image: posTraumato },
  { name: "Fisioterapia na Prescrição de Exercícios Terapêuticos", image: posPrescricao },
];

const BONUSES = [
  {
    title: "Star Cursos",
    image: bonusStarCursos,
    desc: "A maior biblioteca online de cursos em fisioterapia musculoesquelética.",
    points: ["+120 cursos com certificação", "+50 professores", "Atualizações mensais"],
  },
  {
    title: "Fisio Empreende",
    image: bonusFisioEmpreende,
    desc: "A formação complementar\npara o fisio moderno.",
    points: ["Gestão de consultório e clínica", "Carreira e Posicionamento Digital", "Finanças pessoais"],
  },
  {
    title: "FEO IA · Agente Fukusawa",
    image: bonusFeoIa,
    desc: "Para apoiar seu raciocínio clínico e decisões baseadas em evidências.",
    points: ["Treinado com +1.000 horas de conteúdo FEO", "Baseada em evidências", "Manual de uso da ferramenta"],
  },
  {
    title: "Comunidade FEO",
    image: bonusComunidade,
    desc: "Acesso à comunidade exclusiva\nde alunos FEO.",
    points: ["Networking com fisios de todo o Brasil", "Discussão de casos clínicos", "Aulas e mentorias exclusivas"],
  },
];


function useCountdown() {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    // Próximo 23:59 no horário de Brasília (-03:00); reseta automaticamente todo dia.
    const getNextTarget = () => {
      const now = new Date();
      // Hora atual em Brasília
      const brtNow = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const y = brtNow.getUTCFullYear();
      const m = brtNow.getUTCMonth();
      const d = brtNow.getUTCDate();
      // 23:59 BRT = 02:59 UTC do dia seguinte
      let target = Date.UTC(y, m, d, 23 + 3, 59, 0);
      if (target <= now.getTime()) target += 24 * 60 * 60 * 1000;
      return target;
    };
    let target = getNextTarget();
    const tick = () => {
      const now = Date.now();
      if (now >= target) target = getNextTarget();
      const diff = Math.max(0, target - now);
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function PageBody() {
  // Dispara "ViewContent" no dataLayer (GTM → Meta Ads) para qualquer clique
  // em CTA que aponte para o WhatsApp (nimble.li). A campanha de leads no Meta
  // está otimizada para esse evento de conversão.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href.includes("nimble.li")) return;
      const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: "ViewContent",
        content_name: "Projeto Fisio Completo",
        content_category: "fisiocompleto",
        source_page: "/fisiocompleto",
        cta_destination: href,
      });
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      
      <Hero />
      <CountdownStrip />
      <ForWho />
      <PosGrid />
      <BonusStack />
      <WhatHappensInGroup />
      <Testimonials />
      <FinalCTA />
      <CountdownStrip />
      <TeamVerifier />
      <FAQ />

      <Footer />
      <FloatingCTA />
    </div>
  );
}

function ScarcityBar() {
  return (
    <div className="bg-gradient-gold text-gold-foreground">
      <div className="container mx-auto flex items-center justify-center gap-2 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-wider sm:text-xs">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>ATENÇÃO: APENAS 10 VAGAS</span>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-hero">
      <div className="absolute inset-0 grid-bg opacity-30" />
      {/* glow orbs */}
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-accent/20 blur-[120px]" />

      <div className="container relative mx-auto flex flex-col items-center px-4 py-10 text-center lg:py-16">
        <img decoding="async"
          src={logoImg}
          alt="Projeto Fisio Completo"
          className="mb-10 h-auto w-56 drop-shadow-[0_8px_40px_rgba(120,200,255,0.45)] sm:w-72 lg:w-96"
          width={1920}
          height={400}
        />

        <h1 className="max-w-4xl text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          A <span className="text-gradient-primary">virada de chave</span> definitiva na carreira do fisioterapeuta moderno
        </h1>

        <p className="mt-6 max-w-2xl text-pretty text-base text-muted-foreground sm:text-lg">
          Fale agora com o nosso time e descubra a oferta exclusiva que junta Pós-graduações EAD + Star Cursos + Agente IA Fukusawa + Empreendedorismo.<br />
          <span className="whitespace-nowrap text-sm font-semibold text-gold sm:text-lg">Condição especial revelada no atendimento.</span>
        </p>

        <div className="mt-10 flex flex-col items-center gap-3">
          <CtaAction
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-primary px-7 py-4 text-base font-bold text-primary-foreground shadow-glow transition hover:scale-[1.02]"
          >
            <MessageCircle className="h-5 w-5" />
            Garanta sua vaga
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
          </CtaAction>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" /> Exclusivo para fisioterapeutas
          </div>
        </div>

        <div className="mt-14 grid w-full max-w-2xl grid-cols-3 gap-4 border-t border-border/60 pt-8 text-sm">
          <Stat n="+10 mil" l="Alunos formados" />
          <Stat n="MEC" l="Pós reconhecidas" />
          <Stat n="100%" l="Online" />
        </div>
      </div>
    </section>
  );
}

function CountdownStrip() {
  const t = useCountdown();
  return (
    <section className="relative border-y border-gold/30 bg-gradient-to-r from-deep via-background to-deep">
      <div className="container mx-auto flex flex-col items-center gap-6 px-4 py-10 text-center lg:flex-row lg:justify-between lg:text-left">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-gold text-gold-foreground shadow-gold">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <div className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.15em] text-gold sm:text-xs sm:tracking-[0.2em]">
              OFERTA EXCLUSIVA
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Apenas <strong className="text-foreground">10 vagas</strong>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 sm:gap-3" suppressHydrationWarning>
          {[
            { v: t.d, l: "Dias" },
            { v: t.h, l: "Horas" },
            { v: t.m, l: "Min" },
            { v: t.s, l: "Seg" },
          ].map((b) => (
            <div
              key={b.l}
              className="min-w-[64px] rounded-xl border border-gold/30 bg-card/80 px-3 py-2 text-center shadow-card backdrop-blur sm:min-w-[78px] sm:px-4 sm:py-3"
            >
              <div className="font-mono text-3xl font-bold text-gradient-gold sm:text-4xl">
                {String(b.v).padStart(2, "0")}
              </div>
              <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {b.l}
              </div>
            </div>
          ))}
        </div>

        <CtaAction
          className="inline-flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:bg-primary/20"
        >
          <MessageCircle className="h-4 w-4" /> Garantir vaga
        </CtaAction>
      </div>
    </section>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div className="text-xl font-bold text-foreground sm:text-2xl">{n}</div>
      <div className="text-xs text-muted-foreground">{l}</div>
    </div>
  );
}

function ForWho() {
  const items = [
    "Você é fisio e sente que parou no tempo enquanto o mercado evoluiu",
    "Você quer uma pós reconhecida pelo MEC, mas o investimento sempre te trava",
    "Você quer atender com mais segurança clínica e cobrar o que vale",
    "Você quer ir além da técnica e começar a pensar no seu negócio",
  ];
  return (
    <section className="border-y border-border/40 bg-deep/60">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Users className="h-3 w-3" /> Para quem é o Projeto Fisio Completo
          </span>
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Se você se reconhece em <span className="text-gradient-primary">qualquer uma dessas situações</span>, esse projeto é pra você!
          </h2>
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
          {items.map((i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-gradient-card p-5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <p className="text-sm text-foreground sm:text-base">{i}</p>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-muted-foreground">
          O Projeto Fisio Completo é a virada de chave que junta tudo o que o fisio moderno precisa em um só movimento. <strong className="text-foreground">Fale com nosso time e descubra o investimento.</strong>
        </p>
      </div>
    </section>
  );
}

function PosGrid() {
  return (
    <section className="relative">
      <div className="container mx-auto px-4 pb-16 pt-6 lg:pb-24 lg:pt-10">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Pós-graduações <span className="text-gradient-primary">reconhecidas pelo MEC</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            A condição especial vale para qualquer uma das 6 pós da Fisio em Ortopedia. Qual delas combina com a próxima fase da sua carreira?
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gold">
            <Lock className="h-3 w-3" /> CONDIÇÃO REVELADA NO WHATSAPP
          </div>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {POS.map((p) => (
            <div
              key={p.name}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:border-primary/50 hover:shadow-glow"
            >
              <img
                src={p.image}
                alt={p.name}
                className="h-auto w-full"
                width={1920}
                height={1080}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BonusStack() {
  return (
    <section className="border-y border-border/40 bg-deep/60">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
            <Gift className="h-3 w-3" /> O que vem junto
          </span>
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Sua pós + <span className="text-gradient-primary">4 produtos exclusivos</span> que aceleram a sua carreira
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Tudo o que um fisioterapeuta precisa para se especializar, se manter atualizado, atender melhor e empreender, em um só lugar!
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-2xl gap-6 sm:grid-cols-2 lg:max-w-7xl lg:grid-cols-4">
          {BONUSES.map((b) => (
            <div
              key={b.title}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-gradient-card p-6 transition hover:border-primary/50 hover:shadow-glow lg:aspect-auto aspect-square"
            >
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary p-2 text-primary-foreground shadow-glow">
                  <img loading="lazy" decoding="async" src={b.image} alt={b.title} className="h-full w-full object-contain" />
                </div>
                <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">
                  <Lock className="h-3 w-3" /> Incluso
                </span>
              </div>
              <h3 className="mt-5 text-xl font-bold tracking-tight">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.desc}</p>
              <ul className="mt-5 space-y-2">
                {b.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CertificationsTease() {
  return (
    <section className="relative">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="mx-auto max-w-5xl rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/10 via-background to-background p-8 shadow-gold lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
                <Award className="h-3 w-3" /> Etapa 3 · Surpresa no checkout
              </span>
              <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl">
                E ainda tem <span className="text-gradient-gold">5 certificações</span> esperando por você no carrinho.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Direto no checkout, você poderá adicionar todas as nossas certificações com uma <strong className="text-gold">condição especial</strong> reservada apenas para essa ação. A chance perfeita de montar um currículo blindado.
              </p>

              <div className="mt-6 grid gap-2 text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-gold" /> 5 certificações disponíveis</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-gold" /> Aplicação automática no carrinho</div>
                <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-gold" /> Valor revelado no atendimento</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="relative rounded-2xl border border-border bg-gradient-card p-4 text-center"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-gold text-gold-foreground shadow-gold">
                    <Award className="h-6 w-6" />
                  </div>
                  <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Certificação {i + 1}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] font-semibold text-gold">
                    <Lock className="h-2.5 w-2.5" /> Oferta exclusiva
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-dashed border-gold/40 p-4 text-center text-xs text-muted-foreground sm:flex sm:items-center sm:justify-center">
                Disponível somente no checkout
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatHappensInGroup() {
  const steps = [
    {
      icon: MessageCircle,
      title: "1. Clique no botão abaixo",
      desc: "Para conhecer melhor o projeto e receber mais informações.",
    },
    {
      icon: EyeOff,
      title: "2. Converse com a nossa equipe",
      desc: "A sua inscrição será realizada exclusivamente com o nosso acompanhamento.",
    },
    {
      icon: Gift,
      title: "3. Garanta a sua vaga",
      desc: "Fez sentido para você? Então é hora de reservar o seu lugar.",
    },
  ];
  return (
    <section className="border-y border-border/40 bg-deep/60">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
            <Lock className="h-3 w-3" /> OFERTA EXCLUSIVA
          </span>
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            Como <span className="text-gradient-primary">garantir sua vaga</span>
          </h2>

          <p className="mt-4 text-muted-foreground">
            A oferta do Projeto Fisio Completo é exclusiva e limitada, são apenas 10 vagas!
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {steps.map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-gradient-card p-6 transition hover:border-primary/50 hover:shadow-glow">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-glow">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-bold tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <CtaAction
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-glow transition hover:scale-105 sm:text-lg"
          >
            <MessageCircle className="h-5 w-5" />
            Garanta sua vaga
          </CtaAction>
        </div>

      </div>
    </section>
  );
}


function Testimonials() {
  const items = [
    {
      n: "Amanda de Moraes Cardoso",
      badges: [{ l: "Especialização Dor", c: "violet" }, { l: "Star", c: "amber" }],
      d: "13 de jan.",
      q: "Depois da Especialização em Dor para fisioterapeutas, minha prática clínica nunca mais foi a mesma porque o conhecimento é a base do desenvolvimento de todo ser humano. Aprender com profissionais que admiro fez meu entusiasmo pela profissão ser notado nos meus atendimentos, transmitindo maior confiança e segurança, o que proporcionou melhores resultados aos pacientes, sendo esse meu maior objetivo! Sigo atenta a proporcionar uma experiência com embasamento a todos aqueles que confiam a mim, a oportunidade...",
    },
    {
      n: "William de Grandi Campmany",
      badges: [{ l: "Pós Geronto", c: "amber" }, { l: "Certificações", c: "emerald" }, { l: "+5", c: "muted" }],
      d: "18 de jan.",
      q: "Depois da pós em Esporte e Atividade Física, minha prática clínica nunca mais foi a mesma porque, mesmo ainda não terminado a pós, consigo associar com mais facilidade minha vivência teórica e prática de fisio com a minha outra graduação que é a de profissional de educação física, me tornando um profissional mais completo. Me ajudou, também, a me atualizar de algumas informações já ultrapassadas e a relembrar tópicos importantes.\n\nÉ acabar saindo de um pensamento em que muitas vezes só pensa em tratar a lesão e mudar o foco de maneira mais \"rápida\" voltado para a função, progressão de carga e o retorno para o esporte como forma de remédio físico, cognitivo e psicológico.",
    },
    {
      n: "Tais Sandi Tibes Rosa",
      badges: [{ l: "Especialização Dor", c: "violet" }],
      d: "14 de jan.",
      q: "O maior \"click\" foi entender que reduzir dor não depende apenas de corrigir estruturas, mas de reconhecer o mecanismo predominante da dor em cada paciente. Isso me fez valorizar muito mais a educação em dor, o manejo da carga, a progressão adequada de exercícios e a escolha criteriosa das técnicas, tornando o tratamento mais eficaz e individualizado.",
    },
    {
      n: "Brenda Freire Borges de Lira",
      badges: [{ l: "Egressos", c: "emerald" }],
      d: "14 de jan.",
      q: "Minha prática clínica nunca mais foi a mesma porque passei a tomar decisões clínicas baseadas em mecanismos de dor. Hoje consigo diferenciar com clareza quando a dor é predominantemente nociceptiva, neuropática ou nociplástica, o que mudou completamente minha avaliação, minha comunicação com o paciente e a escolha das intervenções.\n\nEntendi que reduzir dor não é sinônimo de \"corrigir tecido\", mas de modular o sistema nervoso. Na prática, isso significou parar de insistir em técnicas locais quando não havia ganho clínico e começar a integrar educação em dor, exposição gradual ao movimento, manejo de crenças e estratégias de autorregulação. O resultado foi maior adesão, menos dependência e melhora da qualidade de vida!",
    },
    {
      n: "Vanessa Vieira Gonçalves",
      badges: [{ l: "Egressos", c: "emerald" }],
      d: "22 de jan.",
      q: "Depois da pós em Esporte e Atividade Física, minha prática clínica nunca mais foi a mesma porque consegui aplicar na prática o raciocínio clínico necessário para reabilitar meus pacientes de forma eficiente!\n\nO maior aprendizado prático que tive e levo para a clínica todos os dias é a abordagem personalizada para cada paciente entendendo que cada corpo funciona e responde de uma forma.",
    },
    {
      n: "Ariádne Rocha Corrêa",
      badges: [{ l: "Pós Geronto", c: "amber" }, { l: "Star", c: "amber" }, { l: "+2", c: "muted" }],
      d: "21 de jan.",
      q: "Depois da pós em fisioterapia em Gerontologia minha prática clínica nunca mais foi a mesma porque eu fui inserida nesse universo fascinante. Compreender a heterogeneidade do envelhecimento e como ele impacta cada pessoa de maneira única, e ter acesso a diversas ferramentas de avaliação foram fundamentais para realizar um bom plano de reabilitação para os pacientes.\n\nMeu maior 'click' de aprendizado foi compreender que nosso objetivo principal é reintegrar o paciente à sua vida na comunidade. Percebi que, além de avaliar e rastrear riscos e alterações, o papel fundamental do fisioterapeuta é tornar o paciente o protagonista do seu tratamento: escutar suas queixas, entender seus objetivos e, a partir disso, construir um plano que seja significativo e direcionado para sua autonomia e independência.",
    },
    {
      n: "Luiza Gabriela de Oliveira Quintal",
      badges: [{ l: "Membro contribuidor", c: "amber" }, { l: "Pós Esporte", c: "violet" }, { l: "+2", c: "muted" }],
      d: "28 de out. de 2025",
      q: "Gostaria de utilizar esse espaço não só para dar um feedback, mas também para agradecer!\n\nConcluí a pós em Esporte e Atividade, e foi através da seleção em uma prova que fui aprovada para receber a bolsa de 100%. Em um momento de recém-formada, em que as coisas não são fáceis, agarrei essa oportunidade. Por causa dessa pós-graduação no currículo, atualmente já trabalho com o esporte, como sempre sonhei.\n\nPor isso, gostaria de agradecer aqui na comunidade aos sócios fundadores da FeO (Lucas, Leandro, Rafael e Hélio) e todo o time, que contribuíram muito com o meu crescimento na fisioterapia. E também, em especial, à Diana, que está sempre presente e prestativa com todos os alunos.\n\nSobre o meu feedback para aqueles que estão aqui e possuem apenas os cursos ou certificações da FeO: a pós é maravilhosa! Professores nota mil, tudo baseado em evidências, e um suporte para dúvidas sobre o conteúdo das aulas que responde rápido e com clareza. Com certeza indico para quem pensa em ingressar em uma pós online.\n\nÉ isso! Continuo aqui na comunidade, contribuindo como posso e grata por estar junto com vocês! 🚀💚",
    },
    {
      n: "Yoli Winnig",
      badges: [{ l: "Pós Geronto", c: "amber" }, { l: "Egressos", c: "emerald" }, { l: "+3", c: "muted" }],
      d: "15 de jan.",
      q: "Depois da pós em fisioterapia em Gerontologia minha prática clínica nunca mais foi a mesma porque apesar de já trabalhar com idosos conheci algumas ferramentas de avaliação e aplicativos que não conhecia e estão sendo muito bons para a minha prática. Além de retomar o estudo em relação a fisiologia do envelhecimento. É sempre bom voltar e retomar alguns aprendizados para solidificar melhor as informações.",
    },
  ];

  const badgeClasses: Record<string, string> = {
    amber: "bg-gold/15 text-gold border-gold/30",
    violet: "bg-primary/15 text-primary border-primary/30",
    emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    muted: "bg-muted text-muted-foreground border-border",
  };

  const initials = (name: string) =>
    name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <section className="relative border-y border-border/40 bg-deep/60">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            +10 mil fisioterapeutas já viveram <span className="text-gradient-primary">a virada de chave</span> com a gente.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Depoimentos reais de alunos da FEO
          </p>
        </div>

        <TestimonialsList items={items} badgeClasses={badgeClasses} initials={initials} />
      </div>
    </section>
  );
}

function TestimonialsList({
  items,
  initials,
}: {
  items: { n: string; d: string; q: string; badges: { l: string; c: string }[] }[];
  badgeClasses: Record<string, string>;
  initials: (name: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW_COUNT = 3;
  const visible = expanded ? items : items.slice(0, PREVIEW_COUNT);

  const renderCard = (t: typeof items[number]) => (
    <figure
      key={t.n}
      className="mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-border bg-[#0b1416] shadow-card"
    >
      {/* WhatsApp-style header */}
      <div className="flex items-center gap-3 border-b border-white/5 bg-[#1f2c33] px-4 py-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-primary-foreground">
          {initials(t.n)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 truncate text-sm font-semibold text-white">
            <span className="truncate">{t.n}</span>
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#25d366]" />
          </div>
          <div className="text-[11px] text-white/50">online</div>
        </div>
        
      </div>

      {/* Chat area */}
      <div className="bg-[#0b1416] bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.03),transparent_60%),radial-gradient(circle_at_80%_90%,rgba(37,211,102,0.05),transparent_60%)] px-4 py-5">
        <div className="relative ml-auto max-w-[92%] rounded-2xl rounded-tr-sm bg-[#005c4b] px-3.5 py-2.5 text-sm leading-relaxed text-white shadow-md">
          <blockquote className="whitespace-pre-line">{t.q}</blockquote>
          <div className="mt-1.5 flex items-center justify-end gap-1 text-[10px] text-white/60">
            <span>{t.d}</span>
            <svg viewBox="0 0 16 11" className="h-2.5 w-3.5 fill-[#53bdeb]" aria-hidden="true">
              <path d="M11.071.653a.5.5 0 0 1 .15.692l-5.5 8.5a.5.5 0 0 1-.764.085l-3-3 .708-.708 2.566 2.566L10.379.803a.5.5 0 0 1 .692-.15Z"/>
              <path d="M15.071.653a.5.5 0 0 1 .15.692l-5.5 8.5a.5.5 0 0 1-.764.085l-.5-.5.708-.708.066.066L14.379.803a.5.5 0 0 1 .692-.15Z"/>
            </svg>
          </div>
        </div>
      </div>
    </figure>
  );

  return (
    <>
      {expanded ? (
        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3 [column-fill:_balance]">
          {visible.map(renderCard)}
        </div>
      ) : (
        <div className="relative mt-12 grid max-h-[360px] gap-5 overflow-hidden sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((t) => (
            <div key={t.n} className="self-start">
              {renderCard(t)}
            </div>
          ))}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-deep/95 to-transparent" />
        </div>
      )}

      {items.length > PREVIEW_COUNT && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:bg-primary/20"
          >
            {expanded ? "Ver menos" : `Ver mais depoimentos`}
            <ArrowRight className={`h-4 w-4 transition ${expanded ? "-rotate-90" : "rotate-90"}`} />
          </button>
        </div>
      )}
    </>
  );
}

function FinalCTA() {
  return (
    <section id="cta" className="relative overflow-hidden border-t border-border/40">
      <div className="container mx-auto px-4 py-20 lg:py-28">
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-primary/40 bg-gradient-primary p-10 text-center shadow-glow lg:p-16">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative">
            <div className="mx-auto mb-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gradient-gold px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow-gold ring-1 ring-gold/60 sm:px-4 sm:text-xs">
              <Lock className="h-3 w-3 flex-shrink-0 sm:h-3.5 sm:w-3.5" /> APENAS 10 VAGAS · OFERTA EXCLUSIVA
            </div>
            <h2 className="text-balance text-3xl font-extrabold tracking-tight text-primary-foreground sm:text-5xl">
              Sua virada de chave começa agora
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-primary-foreground/80">
              Clique no botão abaixo e fale agora com o nosso time! A oferta com o desconto e os benefícios é apresentada <strong>apenas em atendimento direto</strong>.
            </p>
            <CtaAction
              className="mt-8 inline-flex animate-pulse-ring items-center gap-2 whitespace-nowrap rounded-2xl bg-background px-5 py-3.5 text-sm font-bold text-primary shadow-card transition hover:scale-[1.03] sm:px-8 sm:py-5 sm:text-base"
            >
              <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" /> Garanta sua vaga
              <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </CtaAction>
            <div className="mt-4 text-xs text-primary-foreground/70">
              * Exclusivo para fisioterapeutas formados
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TeamVerifier() {
  // Lista de números oficiais do time (apenas dígitos, com DDI 55)
  const OFFICIAL_NUMBERS = new Set<string>(useTeamNumbers());

  const [raw, setRaw] = useState("");
  const [status, setStatus] = useState<"idle" | "ok" | "fail">("idle");

  function formatBR(digits: string) {
    const d = digits.slice(0, 11);
    if (d.length === 0) return "";
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, "");
    setRaw(digits);
    if (status !== "idle") setStatus("idle");
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (raw.length < 10) {
      setStatus("fail");
      return;
    }
    const full = "55" + raw;
    setStatus(OFFICIAL_NUMBERS.has(full) ? "ok" : "fail");
  }

  return (
    <section className="border-t border-border/40 bg-deep/60">
      <div className="container mx-auto max-w-2xl px-4 py-16 lg:py-20">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            <ShieldCheck className="h-3.5 w-3.5" /> Segurança
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-gradient-card p-6 shadow-card sm:p-8">
          <h3 className="text-center text-xl font-bold text-foreground sm:text-2xl">
            Verifique o número do nosso time
          </h3>
          <p className="mt-1 text-center text-sm text-muted-foreground">
            Digite o número e confirme se ele pertence à nossa equipe
          </p>

          <form onSubmit={handleVerify} className="mt-5 flex flex-col gap-2 rounded-xl border border-border bg-background p-2 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2 px-2">
              <span className="text-xs font-bold uppercase text-muted-foreground">BR</span>
              <input
                type="tel"
                inputMode="numeric"
                value={formatBR(raw)}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                className="w-full bg-transparent py-3 text-base outline-none placeholder:text-muted-foreground"
                aria-label="Número do time"
              />
            </div>
            <button
              type="submit"
              className="rounded-lg bg-primary px-5 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
            >
              Verificar
            </button>
          </form>

          {status === "ok" && (
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-medium text-primary">
              <CheckCircle2 className="h-4 w-4" />
              Este número faz parte do time da FEO
              <span className="ml-1 rounded-md bg-primary px-2 py-0.5 text-[10px] font-bold uppercase text-primary-foreground">Verificado</span>
            </div>
          )}
          {status === "fail" && (
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Este número NÃO pertence ao nosso time
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="border-t border-border/40 bg-deep/60">
      <div className="container mx-auto max-w-3xl px-4 py-16 lg:py-24">
        <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
          Perguntas frequentes
        </h2>
        <div className="mt-10 space-y-3">
          {FAQ_ITEMS.map((i) => (
            <details key={i.q} className="group rounded-xl border border-border bg-gradient-card p-5 open:shadow-card">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-base font-semibold">
                {i.q}
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary transition group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{i.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return <MinimalFooter />;
}

function FloatingCTA() {
  return (
    <CtaAction
      className="fixed bottom-5 right-5 z-50 flex h-14 w-14 animate-pulse-ring items-center justify-center rounded-full bg-gradient-primary text-primary-foreground shadow-glow transition hover:scale-110 sm:h-16 sm:w-16"
      aria-label="Garanta sua vaga"
    >
      <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7" />
    </CtaAction>
  );
}

